import type { AddNoteRequest } from '@contracts/note/AddNoteRequest';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { AddNoteForm } from '@src/notes/AddNoteForm';
import type { AddNoteSchemaType } from '@src/notes/AddNoteSchema';
import { NoteParentType } from '@src/notes/domain/Note';
import { NoteAssembler } from '@src/notes/NoteAssembler';
import {
  NoteContentField,
  NoteContextField,
  SendNoteByMailField,
} from '@src/notes/NoteFields';
import { ModalPermitDescription } from '@src/orders/components/permitList/ModalPermitDescription';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

const OptionalAddNoteSchema = yup.object().shape({
  [NoteContextField.fieldName]: yup.array().of(yup.string()).optional(),
  [NoteContentField.fieldName]: yup.string().trim().optional(),
  [SendNoteByMailField.fieldName]: SendNoteByMailField.validator,
});

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (note?: AddNoteRequest) => void;
  permit: PermitSummary;
}

export const ReplacePermitTypeModal = ({
  open,
  onClose,
  onConfirm,
  permit,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();

  //oxlint-disable-next-line
  const form = useForm<AddNoteSchemaType | any>({
    resolver: yupResolver(OptionalAddNoteSchema),
    defaultValues: {
      [NoteContextField.fieldName]: [],
    },
  });

  const onSubmit = (data: AddNoteSchemaType) => {
    const content = data[NoteContentField.fieldName]?.trim();
    if (!content) {
      onConfirm();
      return;
    }
    onConfirm(
      NoteAssembler.fromForm(
        user.id,
        {
          id: permit.id,
          type: NoteParentType.PERMIT_APPLICATION,
        },
        data,
      ),
    );
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={t('permitApplication.replaceType.title')}
    >
      <form>
        <ModalPermitDescription
          jurisdiction={permit.jurisdiction}
          type={permit.type}
          stateField={permit.statePermitNumber ?? '-'}
        />
        <Stack paddingY={theme.spacing['6']}>
          <Typography variant="body2">
            {t('permitApplication.replaceType.note')}
          </Typography>
        </Stack>
        <Stack paddingY={theme.spacing['6']}>
          <AddNoteForm control={form.control} setValue={form.setValue} />
          <Divider style={{ marginTop: theme.spacing['6'] }} />
        </Stack>
        <CloseConfirmModalFooter
          onClose={onClose}
          confirm={form.handleSubmit(onSubmit)}
        />
      </form>
    </BaseModal>
  );
};
